<template>
  <v-dialog persistent min-height="485px" width="650px" :value="show">
    <v-card :loading="isLoading || isFetchingApproval">
      <div class="px-6 py-4" style="min-height: 338px;">
        <template v-if="!isFetchingApproval">
          <v-card-title class="ma-0 pa-0 mb-4 flex-column align-start">
            <p class="ma-0 pa-0 h6 font-weight-600">
              {{ textTitle }}
            </p>
          </v-card-title>

          <v-card-text class="mt-4 pa-0">
            <p
              v-if="isNewApproval || isApprovingRequest"
              v-html="$t('routines.approval.approval_dialog_description')"
              class="blue-grey--text text--darken-3 mb-4 body-2"
            />

            <template v-else>
              <div class="d-flex align-center justify-start">
                <span
                  class="body-2 blue-grey--text text--darken-3 pa-0 mb-0 font-weight-medium mr-3"
                  >{{ $t('routines.approval.approved_by') }}</span
                >
                <ChipAvatar small :account="creationAccount" />
              </div>

              <div class="d-flex align-center justify-start my-4">
                <span
                  class="body-2 blue-grey--text text--darken-3 pa-0 mb-0 font-weight-medium mr-3"
                  >{{ $t('routines.approval.approved_on') }}</span
                >
                <span class="body-2 pa-0 mb-0 ">
                  {{ creationDate }}
                </span>
              </div>
            </template>

            <div class="d-flex flex-column my-4">
              <span
                class="body-2  blue-grey--text text--darken-3 font-weight-medium"
              >
                {{ $t('common.evidence') }}
              </span>

              <div v-if="!hasMedia && !canEdit" class="my-2">
                <v-chip disabled small>{{ $t('common.no_media') }}</v-chip>
              </div>

              <MediaContainer
                v-else
                class="my-2"
                onlyThumbnails
                :readOnly="!canEdit"
                :images="media"
                @change="media = $event"
              />
            </div>

            <div class="d-flex flex-column my-4">
              <span
                class="body-2 blue-grey--text text--darken-3 font-weight-medium"
              >
                {{ $t('attachments.attachments') }}
              </span>

              <v-progress-circular
                v-if="isFetchingAttachments"
                class="my-2"
                color="primary"
                indeterminate
                :size="24"
                :width="2"
              />

              <template v-else>
                <span v-if="canEdit" class="caption blue-grey--text">
                  {{
                    $t('routines.approval.attachments_description', {
                      maxSizeInMB: $constants.fileAttachments.maxFileSizeInMB,
                    })
                  }}
                </span>

                <div v-if="!hasAttachments && !canEdit" class="my-2">
                  <v-chip disabled small>{{
                    $t('common.no_attachments')
                  }}</v-chip>
                </div>

                <ThumbnailAttachments
                  v-else
                  class="my-2"
                  :readOnly="!canEdit"
                  :attachments="attachments"
                  @change="handleAttachmentsChange"
                />
              </template>
            </div>

            <div
              class="d-flex flex-column my-4"
              data-testid="routineApprovalDialog-section-notes"
            >
              <v-textarea
                v-if="canEdit"
                v-model="notes"
                auto-grow
                data-private
                data-testid="routineApproval-textarea-notes"
                dense
                outlined
                :maxlength="maxlength"
                :counter="maxlength"
                :label="$t('routines.note_other')"
              />

              <template v-else>
                <span
                  class="body-2 blue-grey--text text--darken-3 font-weight-medium"
                >
                  {{ $t('routines.note_other') }}
                </span>

                <p
                  v-if="hasNotes"
                  class="body-2 blue-grey--text text--darken-3 pre-line"
                >
                  {{ notes }}
                </p>

                <div v-else class="my-2">
                  <v-chip disabled small>{{ $t('common.no_notes') }}</v-chip>
                </div>
              </template>
            </div>

            <div
              class="d-flex flex-column my-4"
              data-testid="routineApprovalDialog-section-signature"
            >
              <span
                class="body-2 blue-grey--text text--darken-3 font-weight-medium"
                data-testid="routineApprovalDialog-text-signatureTitle"
              >
                {{ $t('checklist.types.signature') }}
              </span>

              <ApprovalSignatureField
                class="my-2"
                data-testid="routineApprovalDialog-signatureField"
                :account="signatureAccount"
                :loading="isLoading"
                :previewHeight="70"
                :readOnly="!canEdit"
                :signature="signature"
                @delete="handleSignatureDeleted"
                @open="openSignatureDialog"
              />
            </div>
          </v-card-text>

          <v-card-actions
            class="pa-0 mt-6"
            :class="isOwnApproval ? 'justify-space-between' : 'justify-end'"
          >
            <v-btn
              v-if="!isNewApproval && isOwnApproval && !isApprovingRequest"
              text
              color="red"
              rounded
              data-testID="routineApprovalDialog-button-remove"
              @click="showRemoveDialog = true"
            >
              {{ $t('routines.approval.remove_approval') }}
            </v-btn>

            <div>
              <v-btn
                text
                color="blue-grey darken-1"
                rounded
                data-testID="routineApprovalDialog-button-cancel"
                :class="canEdit ? 'me-4' : ''"
                @click="handleDiscardChanges"
              >
                {{
                  canEdit
                    ? $t('common.button.cancel')
                    : $t('common.button.close')
                }}
              </v-btn>
              <v-btn
                v-if="canEdit"
                color="primary"
                class="me-0"
                rounded
                data-testID="routineApprovalDialog-button-save"
                :disabled="isFetchingAttachments"
                :loading="isLoading"
                @click="handleSubmit"
              >
                {{ buttonText }}
              </v-btn>
            </div>
          </v-card-actions>
        </template>
      </div>
    </v-card>

    <ConfirmationModal
      confirmColor="danger"
      :show="showDiscardDialog"
      :title="$t('routines.approval.discard_approval_title')"
      :text="$t('routines.approval.discard_approval_message')"
      :confirmText="$t('common.button.discard')"
      :cancelText="$t('common.button.cancel')"
      @cancel="showDiscardDialog = false"
      @confirm="$emit('close')"
    />

    <RemoveApprovalDialog
      :show="showRemoveDialog"
      :approvalId="approvalId"
      :routineId="routineId"
      @close="showRemoveDialog = false"
      @success="handleRemoveSuccess"
    />

    <div v-if="showSignatureDialog">
      <SignatureDialog
        :title="$t('checklist.signature_dialog_title')"
        :subtitle="$t('checklist.signature_dialog_subtitle')"
        :signerName="signatureSignerName"
        data-testid="routineApprovalDialog-dialog-signature"
        @close="closeSignatureDialog"
        @save="handleSignatureSaved"
      />
    </div>
  </v-dialog>
</template>
<script>
import _ from 'lodash'
import ConfirmationModal from '@/components/Modals/ConfirmationModal.vue'
import RemoveApprovalDialog from '@/components/Routines/RoutineApproval/RemoveApprovalDialog.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import ThumbnailAttachments from '@/components/ThumbnailAttachments/ThumbnailAttachments.vue'
import SignatureDialog from '@/components/Checklists/ChecklistReply/SignatureDialog.vue'
import SignatureForm from '@/components/Checklists/ChecklistReply/SignatureForm.vue'

import mixin from '@/mixins/mixin.vue'
import routineApprovalPermissions from '@/mixins/routineApprovalPermissions.vue'
import { utils } from 'frontend-js-shared'
import api from '@/api'
import { buildAccountFullName } from '@/components/Checklists/ChecklistReply/signatureUtils'
import { APPROVAL_STATUS } from '@/containers/RoutineExecutionView/Approvals/constants'

import { mapGetters } from 'vuex'

export default {
  name: 'RoutineApprovalDialog',

  mixins: [mixin, routineApprovalPermissions],

  props: {
    show: Boolean,
    approval: Object,
    routine: {
      type: Object,
      default: () => ({}),
    },
    routineId: String,
    shouldFetchCurrentAccountApproval: Boolean,
    shouldApproveRequest: Boolean,
  },

  components: {
    ThumbnailAttachments,
    ChipAvatar,
    ConfirmationModal,
    MediaContainer,
    RemoveApprovalDialog,
    SignatureDialog,
    ApprovalSignatureField: SignatureForm,
  },

  data() {
    return {
      APPROVAL_STATUS,
      attachments: [],
      fetchedApproval: null,
      initialAttachments: [],
      initialNotes: this.approval?.notes || '',
      isFetchingApproval: false,
      isFetchingAttachments: false,
      isLoading: false,
      isRemovingApproval: false,
      maxlength: 1200,
      media: this.approval?.media || [],
      notes: this.approval?.notes || '',
      signature: this.approval?.signature || null,
      showDiscardDialog: false,
      showRemoveDialog: false,
      showSignatureDialog: false,
    }
  },

  mounted() {
    if (this.shouldFetchCurrentAccountApproval) {
      this.fetchCurrentAccountApproval()
    }

    if (!this.isNewApproval) {
      this.fetchAttachments()
    }
  },

  watch: {
    'internalApproval.id'() {
      if (!this.isFetchingAttachments) {
        this.fetchAttachments()
      }
    },
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    internalApproval() {
      return this.approval || this.fetchedApproval
    },

    isOwnApproval() {
      return (
        utils.getEntityId(this.internalApproval?.createdBy) === this.account.id
      )
    },

    isNewApproval() {
      return !this.internalApproval?.id
    },

    isApprovingRequest() {
      return this.shouldApproveRequest
    },

    approvalPermissions() {
      return this.getRoutineApprovalPermissions({
        routineApproval: this.internalApproval,
        routine: this.routine,
      })
    },

    canEdit() {
      return Boolean(
        this.isOwnApproval ||
          this.isNewApproval ||
          this.isApprovingRequest ||
          this.approvalPermissions.edit
      )
    },

    textTitle() {
      if (this.isNewApproval || this.isApprovingRequest) {
        return this.$t('routines.approval.create_approval')
      }

      if (this.isOwnApproval || this.approvalPermissions.edit) {
        return this.$t('routines.approval.edit_approval')
      }

      return this.$t('routines.approval.approval_details')
    },

    buttonText() {
      return this.isNewApproval || this.isApprovingRequest
        ? this.$t('common.approve')
        : this.$t('common.button.update')
    },

    approvalId() {
      return this.internalApproval?.id
    },

    creationDate() {
      return utils.dates.formatDate(
        this.internalApproval?.createdAt || new Date(),
        'MMM D YYYY, h:mm A'
      )
    },

    creationAccount() {
      return this.internalApproval?.createdBy || this.account
    },

    signatureAccount() {
      // Prefer the targeted approver on the approval record. Falls back to
      // creationAccount for historical records where `approver` may not be
      // populated by the backend, then to the current account as a last-resort
      // safety net. The middle fallback (creationAccount) is defensive only
      // and can be removed once we confirm no pre-GA record reaches the read
      // view with `approver === undefined/null` (see
      // docs/technical-decisions/2026-07-08-routine-approvals-flow.md,
      // decision 10).
      const account =
        this.internalApproval?.approver ?? this.creationAccount

      return account ?? this.account
    },

    signatureSignerName() {
      return buildAccountFullName(this.signatureAccount)
    },

    removeApprovalTitle() {
      return this.$t('routines.approval.remove_approval_title')
    },

    removeApprovalText() {
      return this.$t('routines.approval.remove_approval_message')
    },

    hasMedia() {
      return !_.isEmpty(this.media)
    },

    hasNotes() {
      return !_.isEmpty(this.notes.trim?.())
    },

    hasAttachments() {
      return !_.isEmpty(this.attachments)
    },

    initialSignatureMediaId() {
      return utils.getEntityId(this.internalApproval?.signature) ?? null
    },

    hasDraftChanges() {
      if (this.notes !== this.initialNotes) return true

      const currentSignatureId =
        utils.getEntityId(this.signature) ?? this.signature?.mediaId ?? null

      return currentSignatureId !== this.initialSignatureMediaId
    },

    attachmentsToLink() {
      return this.attachments.filter(attachment => !attachment.id)
    },

    attachmentsToUnlink() {
      const attachmentsIds = this.attachments.map(attachment =>
        utils.getEntityId(attachment)
      )

      return this.initialAttachments.filter(
        initialAttachment => !attachmentsIds.includes(initialAttachment.id)
      )
    },
  },

  methods: {
    handleSubmit() {
      if (this.isApprovingRequest) {
        return this.approveRequest()
      }

      return this.isNewApproval ? this.createApproval() : this.updateApproval()
    },

    async approveRequest() {
      try {
        this.isLoading = true
        const payload = { ...this.getApprovalPayload(), status: APPROVAL_STATUS.APPROVED }

        await api.routine.updateRoutineApproval(
          this.organizationId,
          this.routineId,
          this.approvalId,
          payload
        )

        await this.linkAttachments()
        await this.unlinkAttachments()

        this.showSuccess(this.$t('routines.approval.approval_success'))
        this.$emit('approved')
      } catch (err) {
        this.showError(this.$t('routines.approval.approval_creation_failed'))
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.$emit('close')
      }
    },

    getApprovalPayload() {
      const payload = {
        notes: this.notes,
        media: this.media?.map?.(utils.getEntityId) || [],
      }

      payload.signature = utils.getEntityId(this.signature) ?? this.signature?.mediaId ?? null

      return payload
    },

    openSignatureDialog() {
      this.showSignatureDialog = true
    },

    closeSignatureDialog() {
      this.showSignatureDialog = false
    },

    handleSignatureSaved(signature) {
      this.signature = signature?.media || signature
      this.closeSignatureDialog()
    },

    handleSignatureDeleted() {
      this.signature = null
    },

    handleDiscardChanges() {
      if (this.hasDraftChanges) {
        return (this.showDiscardDialog = true)
      }

      this.notes = ''
      this.showDiscardDialog = false
      this.$emit('close')
    },

    async createApproval() {
      try {
        this.isLoading = true
        const response = await api.routine.createRoutineApproval(
          this.organizationId,
          this.routineId,
          this.getApprovalPayload()
        )

        this.fetchedApproval = response.data
        this.signature = response?.data?.signature || null

        await this.$nextTick()
        await this.linkAttachments()

        this.showSuccess(this.$t('routines.approval.approval_success'))
        this.$emit('approved')
      } catch (err) {
        this.showError(this.$t('routines.approval.approval_creation_failed'))
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.$emit('close')
      }
    },

    async updateApproval() {
      try {
        if (!this.approvalId) return
        this.isLoading = true

        await api.routine.updateRoutineApproval(
          this.organizationId,
          this.routineId,
          this.internalApproval.id,
          this.getApprovalPayload()
        )

        await this.linkAttachments()
        await this.unlinkAttachments()

        this.showSuccess(
          this.$t('routines.approval.approval_updated_successfully')
        )
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        this.showError(this.$t('routines.approval.approval_update_failed'))
      } finally {
        this.isLoading = false
      }
    },

    async fetchCurrentAccountApproval() {
      try {
        this.isFetchingApproval = true
        const response = await api.routine.getRoutineApprovals(
          this.organizationId,
          this.routineId,
          {
            filters: { createdBy: this.account.id },
            relations: this.getCurrentApprovalRelations(),
          }
        )

        this.fetchedApproval = response?.data?.results?.[0] ?? null
        this.initialNotes = this.fetchedApproval?.notes ?? ''
        this.notes = this.fetchedApproval?.notes ?? ''
        this.media = [...(this.fetchedApproval?.media ?? [])]
        this.signature = this.fetchedApproval?.signature || null
      } catch (err) {
        this.fetchedApproval = null
        this.signature = null
      } finally {
        this.isFetchingApproval = false
      }
    },

    async handleRemoveSuccess() {
      this.$emit('removed')
      this.$emit('close')
    },

    getCurrentApprovalRelations() {
      const relations = ['createdBy', 'media']

      relations.push('signature')

      return relations
    },

    async fetchAttachments() {
      try {
        this.isFetchingAttachments = true

        const response = await api.routine.getRoutineApprovalAttachments(
          this.organizationId,
          this.approvalId
        )

        this.initialAttachments = [...(response?.data || [])]
        this.attachments = [...(response?.data || [])]
      } catch (error) {
        this.initialAttachments = []
        this.attachments = []
      } finally {
        this.isFetchingAttachments = false
      }
    },

    handleAttachmentsChange(attachments) {
      this.attachments = [...attachments]
    },

    linkAttachments() {
      if (!this.approvalId || _.isEmpty(this.attachmentsToLink)) {
        return Promise.resolve()
      }

      try {
        const promises = this.attachmentsToLink.map(attachment =>
          api.routine.linkAttachmentToRoutineApproval(
            this.organizationId,
            this.approvalId,
            attachment?.media?.id
          )
        )

        return Promise.all(promises)
      } catch (error) {
        this.showError(this.$t('attachments.uploading_the_file_failed'))
      }
    },

    unlinkAttachments() {
      if (!this.approvalId || _.isEmpty(this.attachmentsToUnlink)) {
        return Promise.resolve()
      }

      try {
        const promises = this.attachmentsToUnlink.map(attachment =>
          api.routine.unlinkAttachmentFromRoutineApproval(
            this.organizationId,
            this.approvalId,
            attachment?.id
          )
        )

        return Promise.all(promises)
      } catch (error) {
        this.showError(this.$t('routines.approval.approval_update_failed'))
      }
    },
  },
}
</script>
